/**
 * Principal — WHO is asking, and WHAT they may do.
 *
 * Foundations 1 (Users rail), B1-T2. principles.md #1 ("one store, one
 * resolver, one owner per noun") and #4 ("nothing fails open").
 *
 * WHAT A PRINCIPAL IS
 *   The resolved authority of one user inside one organization: the role keys
 *   they hold there (direct assignments plus the roles their groups hold) and
 *   the union of those roles' grants. It is a VALUE — computed by
 *   `policy/principal.ts` from the roles table, then handed to the pure
 *   `can(principal, action, resource)` in `policy/can.ts`.
 *
 * WHAT IT IS NOT
 *   Not a session, not a JWT, not `request.user`. `request.user` says which
 *   user and which org the bearer token asserts; a Principal says what that
 *   pairing is allowed to do, and only the store can answer that.
 *
 * WHY IT IS A ZOD SCHEMA
 *   Zod is the type source (CLAUDE.md). The shape crosses a package boundary
 *   (`GET /users/me` returns roleKeys + permissions from exactly these
 *   fields in B1-T3), so it is declared once, here, and both sides read it.
 *
 * WHERE THIS SHAPE DIFFERS FROM THE B1-T2 TASK CONTRACT (review R09)
 *   The contract declared `{ userId, organizationId, roleKeys, permissions,
 *   departmentScope, impersonator? }`. What ships is that, minus the last two,
 *   plus `roles[]`. Recorded here so B1-T3 reads it rather than discovering it:
 *
 *   - `departmentScope` — SHIPPED IN B2a-T1, exactly as the note below it
 *     required: the field, the store it is read from
 *     (`user_department_memberships` + `user_organizations.all_departments`,
 *     migration c28 — BOTH halves per tenant, review B2aT1-BF-01),
 *     the resolver that fills it and the visibility function that reads it
 *     (`canSee` in `api/src/policy/visibility.ts`) all landed in one change. It
 *     is REQUIRED, never optional: an absent scope would be read as "unknown,
 *     so allow", and the only safe unknown is the most restrictive scope
 *     (`DEPARTMENT_SCOPE_NONE`), which is a value, not an absence.
 *   - `impersonator` — DEFERRED. Impersonation is a property of the SESSION,
 *     not of the authority: `services/impersonation.service.ts` mints a token
 *     that says who is being impersonated, and the resolver answers about the
 *     user that token names. Putting the impersonator on the Principal would
 *     make `policy/principal.ts` read the JWT, which is the one input it is
 *     defined not to have (see "WHAT IT IS NOT" above). The Users-rail
 *     impersonation work owns the field and decides where it lives.
 *   - `roles[]` — ADDED. `roleKeys` alone cannot answer "why do I hold this?"
 *     on the Effective Permissions tab, and it cannot distinguish the SHIPPED
 *     owner row from a tenant role keyed `owner` — a distinction
 *     `policy/can.ts` needs for the two non-delegable Owner actions, because
 *     the store permits both rows (review R07 / BF-08).
 *
 * WHAT B1-T2c ADDED, AND WHY IT IS NOT DECORATION
 *   `roles[]` gained `description`, `viaGroupName`, `permissions` (that role's
 *   own slice of the union) and `assignment` (the row it came in on).
 *   `GET /users/:id/permissions` and `GET /users/:id/role-assignments` used to
 *   read `user_role_assignments` + `roles` themselves — a SECOND resolver that
 *   honoured expired assignments, another tenant's roles, retired roles and
 *   inactive ones, and dropped every non-CRUD grant. The Users page therefore
 *   listed authority the gate refused. The fix is that those routes project
 *   THIS object and read no table, which is only possible if the resolver
 *   carries what the projection renders. One read, one answer.
 */

import { z } from 'zod';
import { RbacActionSchema, RbacResourceSchema } from './permissions.js';
import { RoleKeySchema } from './roles.js';
import { DepartmentScopeSchema } from './departments.js';

/**
 * Where one of a principal's role keys came from. `role` is a direct
 * assignment on `user_role_assignments`; `group` is inherited through
 * `group_role_assignments` from a group the user belongs to.
 */
export const PrincipalRoleSourceSchema = z.enum(['role', 'group']);
export type PrincipalRoleSource = z.infer<typeof PrincipalRoleSourceSchema>;

/** A `<action>:<resource>` grant key, validated against the catalogue. */
export const PrincipalPermissionSchema = z
  .string()
  .refine(
    (value) => {
      const cut = value.lastIndexOf(':');
      if (cut <= 0) return false;
      return (
        RbacActionSchema.safeParse(value.slice(0, cut)).success &&
        RbacResourceSchema.safeParse(value.slice(cut + 1)).success
      );
    },
    { message: 'Not a permission key in the catalogue' },
  );

/**
 * The assignment row a held role came in on.
 *
 * B1-T2c. `GET /users/:id/role-assignments` renders exactly these columns, and
 * it may not read them itself: an assignment the resolver DROPPED (expired,
 * pointing at another tenant's role, pointing at a retired role with no
 * successor) must not appear on the page that answers "what can this person
 * do", because `can()` has already answered "nothing" for it. Carrying the
 * provenance on the role the resolver honoured is what makes one read serve
 * both — the alternative was a second query in the route, which is the defect
 * B1-T2c deleted.
 *
 * For a role held through a GROUP the row is the `group_role_assignments` one:
 * it has no `source` and no `expiresAt`, so both are null there.
 */
export const PrincipalRoleAssignmentSchema = z.object({
  /** The `user_role_assignments` / `group_role_assignments` row id. */
  id: z.string().uuid(),
  /**
   * How the assignment was made — `manual | sso | inherited | migration |
   * system` (c24 made the column TEXT with a CHECK). Null for a group-held
   * role, whose table has no such column.
   */
  source: z.string().nullable(),
  /**
   * ISO 8601, or null when the row carries no readable timestamp.
   *
   * NULLABLE, deliberately (review R6). It used to fall back to
   * `new Date(0)`, so a missing or unparseable `assigned_at` reached the wire
   * as `1970-01-01T00:00:00.000Z` and the Roles & Groups tab rendered an
   * invented date as a fact — and sorted that row first. `expiresAt` beside it
   * has always passed null through; this does the same. The column is NOT NULL
   * in both tables, so null here means "the store did not give us one", which
   * is a thing to render as `—`, never as a date.
   */
  assignedAt: z.string().nullable(),
  /** Who made the assignment, when the row recorded it. */
  assignedById: z.string().uuid().nullable(),
  /**
   * ISO 8601, always in the FUTURE when it is set: the resolver drops a lapsed
   * assignment, so an expiry in the past cannot reach a principal.
   */
  expiresAt: z.string().nullable(),
});
export type PrincipalRoleAssignment = z.infer<
  typeof PrincipalRoleAssignmentSchema
>;

/**
 * One role a principal holds, with enough identity for the Effective
 * Permissions surface to say WHY a grant is held (B1-T3 renders it).
 */
export const PrincipalRoleSchema = z.object({
  id: z.string().uuid(),
  key: RoleKeySchema,
  name: z.string(),
  /** The role's own description, as the store holds it. */
  description: z.string().nullable(),
  /** True for one of the five shipped rows (`organization_id IS NULL`). */
  isPlatform: z.boolean(),
  source: PrincipalRoleSourceSchema,
  /**
   * The group this role came through, when `source === 'group'`.
   * NULL for a direct assignment.
   */
  viaGroupId: z.string().uuid().nullable(),
  /** That group's display name, so a surface can say `group:<Name>`. */
  viaGroupName: z.string().nullable(),
  /**
   * Set when the assignment pointed at a RETIRED role that names a successor
   * (`superseded_by`): the id of the role whose grants were actually used.
   * upgrade-model.md §3 — a retired role with no successor contributes
   * nothing, and this stays null because nothing was contributed.
   */
  resolvedFromRoleId: z.string().uuid().nullable(),
  /**
   * Whether the role the ASSIGNMENT ROW names is one of the shipped platform
   * rows — which is NOT always what `isPlatform` says.
   *
   * `isPlatform` describes the EFFECTIVE role (the successor, when the
   * assignment was re-pointed). The two differ exactly when a retired TENANT
   * role names a shipped successor, and the difference decides a real thing:
   * `DELETE /users/:userId/roles/:roleId` acts on the row on file, refuses a
   * platform role, and the Roles & Groups tab hides its Remove control for
   * `roleType: 'system'`. Reading the successor's flag there hid the control
   * for an assignment that IS removable (review BF-01 variant 3).
   *
   * Same rule as `isPlatform`: computed as `is_platform AND organization_id IS
   * NULL`, never trusted from the flag alone — the store permits a tenant row
   * with `is_platform = true`.
   */
  assignedIsPlatform: z.boolean(),
  /**
   * THIS role's grants — the slice of the principal's union it contributed.
   *
   * B1-T2c: "which role gave me this?" is answered from here and nowhere else.
   * `permissions` on the principal is the union of exactly these arrays, so a
   * grant can never be attributed to a role that did not carry it.
   */
  permissions: z.array(PrincipalPermissionSchema),
  /**
   * Did the role's STORED grants carry a non-delegable Owner key — before
   * `effectiveGrants` stripped it from `permissions` above?
   *
   * B2d-T2, review B2dT2-R1. THIS FIELD EXISTS BECAUSE THE EVIDENCE IS
   * DESTROYED BY THE TIME ANYBODY CAN LOOK. `effectiveGrants` rewrites every
   * role of a principal that is not the shipped platform Owner as
   * `permissions.filter(key => !OWNER_ONLY_PERMISSIONS.has(key))`, and
   * `resolvePrincipal` returns those rewritten roles — so a tenant role CLONED
   * from Owner ("Operations Lead", carrying `factory:reset:tenants`) reaches
   * every reader with the clue already removed. `holdsOwnerAuthority`, the
   * rule that says nobody impersonates an Owner however the authority was
   * granted (decisions.md 2026-09-13 #1), read `permissions` and therefore
   * could never fire against a principal the resolver actually produced: it
   * passed only against hand-built test fixtures of a shape the resolver cannot
   * emit.
   *
   * It is a FACT ABOUT THE ROW, recorded once where the stripping happens, not
   * a second derivation: `effectiveGrants` sets it from the input it is about
   * to filter, and nothing else computes it. `false` for the ordinary role,
   * which is nearly all of them.
   */
  ownerAuthority: z.boolean(),
  /** The assignment row that conferred it. */
  assignment: PrincipalRoleAssignmentSchema,
});
export type PrincipalRole = z.infer<typeof PrincipalRoleSchema>;

/**
 * The resolved principal.
 *
 * `permissions` is the UNION of every contributing role's grants, already
 * de-duplicated and already stripped of anything that does not parse
 * (`parseRoleGrants` drops it — an unreadable grant is not a grant).
 */
export const PrincipalSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  /** Every role key held, direct or through a group. Sorted, de-duplicated. */
  roleKeys: z.array(RoleKeySchema),
  /** The roles behind those keys, for attribution. */
  roles: z.array(PrincipalRoleSchema),
  /** The union of their grants. Sorted, de-duplicated. */
  permissions: z.array(PrincipalPermissionSchema),
  /**
   * The departments this person is scoped to — the SECOND half of the
   * visibility rule (B2a-T1).
   *
   * `can()` answers "may they do this at all"; this answers "to which of the
   * tenant's things". Both halves are needed and neither is sufficient, which
   * is why they travel together on one resolved value: a consumer that had to
   * fetch the scope separately would be a second resolver for the department
   * noun on the day it was written. `canSee()` is the only function that reads
   * it.
   *
   * Resolved on the SAME query as the grants (`policy/principal.ts`), so a
   * principal that exists always carries a scope, and a scope that cannot be
   * read is a `PrincipalUnavailableError`, never an empty one.
   */
  departmentScope: DepartmentScopeSchema,
});
export type PrincipalShape = z.infer<typeof PrincipalSchema>;

/**
 * The AUTHORITY half of `GET /users/me` — the two fields the browser shell
 * gates its affordances on.
 *
 * Foundations 1 (Users rail), B1-T3, review B1T3-R3. Zod is the type source
 * (CLAUDE.md), and this shape crosses a package boundary, so it is declared
 * once. It used to be hand-kept twice: an inline `z.object` in the route's
 * response schema and an untyped `stringArray(body?.roleKeys)` read in
 * `packages/web/src/contexts/auth-context.tsx`. Renaming `roleKeys` on the
 * server left both packages type-checking while every user's role list became
 * `[]` in the browser, with no compile error and no test that caught it.
 *
 * They are plain strings rather than `RoleKeySchema` / `PrincipalPermission
 * Schema` because this is the WIRE shape: a browser on an older bundle must be
 * able to read a response containing a key its copy of the vocabulary does not
 * list, and answer "no" for it, rather than discarding the whole payload.
 */
export const MeAuthoritySchema = z.object({
  /** Every role key the server resolved for this person, shipped or tenant. */
  roleKeys: z.array(z.string()),
  /** The union of those roles' grants — the ONLY thing the shell gates on. */
  permissions: z.array(z.string()),
});
export type MeAuthority = z.infer<typeof MeAuthoritySchema>;

/**
 * Read the authority fields off a `/users/me` body. FAILS CLOSED.
 *
 * A missing, malformed or non-array field yields `[]`, i.e. "holds nothing",
 * never "unknown, so allow" (principles.md #4) — the same rule
 * `parseRoleGrants` applies on the server to a row it cannot parse. Individual
 * non-string entries are dropped rather than voiding the whole array, so one
 * bad element cannot log a user out of every affordance they hold.
 *
 * It walks `MeAuthoritySchema.shape` rather than naming the fields again, so
 * the reader follows a rename of the schema instead of drifting from it.
 */
export function readMeAuthority(body: unknown): MeAuthority {
  const source = (body ?? {}) as Record<string, unknown>;
  const out: Record<string, string[]> = {};
  for (const field of Object.keys(MeAuthoritySchema.shape)) {
    const value = source[field];
    out[field] = Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
      : [];
  }
  return MeAuthoritySchema.parse(out);
}
